import { SelectField, TextField } from "@/components/tool-kit/primitives";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import type { COLLIGATIVE_PROPERTIES_CALCULATOR_STRINGS } from "@/components/tools/colligative-properties-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type Property,
  SOLUTES,
  SOLVENTS,
} from "@/lib/tool-kit/chem-physical/colligative";
import { TEMPERATURE_UNITS } from "@/lib/tool-kit/chem-physical/units";

export type ColligativeStrings =
  typeof COLLIGATIVE_PROPERTIES_CALCULATOR_STRINGS;

export const CUSTOM = "custom";
export const DEFAULT_SIG = 3;
export const TEMPERATURE_OPTIONS = TEMPERATURE_UNITS.map((u) => ({
  value: u.id,
  label: u.symbol,
}));

export function colligativeMessages(
  strings: ColligativeStrings
): Record<string, string> {
  return {
    missing: strings.enterValues,
    unreadable: strings.errorUnreadable,
    nonPositive: strings.errorNonPositive,
    belowZero: strings.errorBelowZero,
    resultBelowZero: strings.errorResultBelowZero,
    wrongDirection: strings.errorWrongDirection,
  };
}

/** The solvent menu, plus a constant and a pure-solvent point when "custom" is chosen. */
export function SolventFields({
  idPrefix,
  property,
  solvent,
  onSolvent,
  k,
  onK,
  point,
  onPoint,
  tempUnit,
  onTempUnit,
  strings,
}: {
  idPrefix: string;
  property: Property;
  solvent: string;
  onSolvent: (value: string) => void;
  k: string;
  onK: (value: string) => void;
  point: string;
  onPoint: (value: string) => void;
  tempUnit: string;
  onTempUnit: (value: string) => void;
  strings: ColligativeStrings;
}) {
  const fmt = useToolNumber();
  const constantName = property === "boil" ? strings.kb : strings.kf;
  const options = [
    ...SOLVENTS.map((entry) => ({
      value: entry.id,
      label: t(strings.solventOption, {
        name: strings.solvents[entry.id as keyof typeof strings.solvents],
        constant: constantName,
        k: fmt(property === "boil" ? entry.kb : entry.kf),
        point: fmt(property === "boil" ? entry.boil : entry.freeze),
      }),
    })),
    { value: CUSTOM, label: strings.solventCustom },
  ];
  return (
    <>
      <SelectField
        hint={strings.solventHint}
        id={`${idPrefix}-solvent`}
        label={strings.solventLabel}
        onChange={onSolvent}
        options={options}
        value={solvent}
      />
      {solvent === CUSTOM ? (
        <>
          <TextField
            hint={strings.customKHint}
            id={`${idPrefix}-k`}
            label={t(strings.customK, { constant: constantName })}
            numeric
            onChange={onK}
            value={k}
          />
          <QuantityInput
            id={`${idPrefix}-point`}
            label={
              property === "boil"
                ? strings.pureBoilingPoint
                : strings.pureFreezingPoint
            }
            onUnitChange={onTempUnit}
            onValueChange={onPoint}
            unit={tempUnit}
            unitLabel={strings.unitTemperature}
            units={TEMPERATURE_OPTIONS}
            value={point}
          />
        </>
      ) : null}
    </>
  );
}

/** The van 't Hoff factor with quick fills from OpenStax Table 11.3. */
export function VantHoffFields({
  idPrefix,
  choice,
  onChoice,
  value,
  onValue,
  strings,
}: {
  idPrefix: string;
  choice: string;
  onChoice: (choice: string, factor: string | null) => void;
  value: string;
  onValue: (value: string) => void;
  strings: ColligativeStrings;
}) {
  const fmt = useToolNumber();
  const options = [
    { value: CUSTOM, label: strings.soluteCustom },
    ...SOLUTES.flatMap((solute) => [
      {
        value: `${solute.id}:ideal`,
        label: t(strings.soluteIdeal, {
          formula: solute.formula,
          i: fmt(solute.ideal),
        }),
      },
      ...(solute.measured === solute.ideal
        ? []
        : [
            {
              value: `${solute.id}:measured`,
              label: t(strings.soluteMeasured, {
                formula: solute.formula,
                i: fmt(solute.measured, { minimumFractionDigits: 1 }),
              }),
            },
          ]),
    ]),
  ];
  const factorOf = (id: string): string | null => {
    const [soluteId, kind] = id.split(":");
    const solute = SOLUTES.find((entry) => entry.id === soluteId);
    if (!solute) {
      return null;
    }
    return String(kind === "measured" ? solute.measured : solute.ideal);
  };
  return (
    <>
      <SelectField
        hint={strings.soluteHint}
        id={`${idPrefix}-solute`}
        label={strings.soluteLabel}
        onChange={(next) => onChoice(next, factorOf(next))}
        options={options}
        value={choice}
      />
      <TextField
        hint={strings.iHint}
        id={`${idPrefix}-i`}
        label={strings.vantHoff}
        numeric
        onChange={onValue}
        value={value}
      />
    </>
  );
}
